/**
 * v0.19.14 — iOS「弹键盘 / 抽屉打开瞬间 modal 顶部露出页面内容」: **真机诊断探针**
 *
 * ## 为什么有这个文件 (三批都失败的原因)
 *   v0.19.04 / v0.19.05 / v0.19.06 连修三批**全部没修对**, 而且**无头环境里复现不出来**
 *   ⇒ 继续「猜机制」是没有出路的 (brief §1.2 的 C1~C6)。
 *   ⇒ 本文件**不修任何东西**, 只做一件事: **把「露出条」的几何量出来**,
 *     一次真机复现就能区分开 E1/E2/E3/E4/E5/E8/E9/E10 哪条是真凶。
 *
 * ## ⛔ 默认关 (对真实用户零影响)
 *   开关 = URL 参数 `?zskbdiag=1` (`new URLSearchParams(location.search).get('zskbdiag') === '1'`)。
 *   ⛔ **没带参数时**: 不进 DOM、不挂监听、不写样式、`window.__zsKbDiag` 连定义都不定义
 *      (brief G1 断言 `document.querySelectorAll('[data-zs-kbdiag]').length === 0`
 *        且 `window.__zsKbDiag === undefined`)。
 *   ⇒ 本文件只被 `js/src/forum/index.js` 的 initializer 调一次, 入口见 `initIosKeyboardDiag()`。
 *
 * ## ⭐ v0.19.14 补记 — **SPA 导航后自动 teardown**
 *   Flarum 是 SPA ⇒ 客户端导航后 URL 里的 `?zskbdiag=1` 会掉, 但页面**不重载**
 *   ⇒ 旧写法「开关只在加载时判一次」会让浮层**飘到后续页面**、盖住正要截的证据,
 *     也让 brief §4 的「退出诊断开关 ⇒ 逐字节还原」在会话内**根本没有实现路径**。
 *   ⇒ 现在常驻 rAF 循环里**节流** (500ms) 复查一次 URL ⇒ 参数一掉就**完整拆除**
 *     (摘监听 / 停 rAF / 移除浮层 / `delete window.__zsKbDiag`)。
 *   详见 `IosKeyboardDiag._maybeTeardown()` 与 `teardown()` 的注释。
 *
 * ## 三路输出 (缺一不可, brief §2.1)
 *   ① **页面浮层**: 视口右上角 (`top: 52px; right: 0`) 的半透明黑底小面板, 新的在上。
 *      ⚠️ **52px 不是 0** —— 辉哥截图里的「露出条」是**通栏**的 (顶部一整条 ≈31~46px),
 *         浮层放 `top: 0` 会**正好盖住证据**。
 *   ② **`window.__zsKbDiag`**: 完整数组 (真机 Safari Web Inspector 里看)。
 *   ③ **console**: 每条 `console.log('[zsKbDiag]', JSON.stringify(row))`。
 *
 * ## ⭐ 与组件②「modal 上边缘钉住」补偿的互不干扰 (brief §2.2 末段)
 *   补偿 (在 `SearchableUserPicker.js` 里) 会往 `.Modal` 上写 inline `style.top`。
 *   ⇒ 补偿生效后, `.Modal` 的 `getBoundingClientRect().top` 已经被**拉回基准**,
 *     直接读它会**看不到原始位移** ⇒ 数据自证。
 *   ⇒ 本探针把 `mdlTop` 定义成「**补偿前**的几何」:
 *        `mdlTop = rect.top − 补偿写入的偏移量(pin.applied)`
 *     并且**额外**把 `mdlRectTop`(原始 rect.top) 与 `pinApplied`(补偿偏移) 一起记下来
 *     ⇒ 三者恒满足 `mdlTop + pinApplied === mdlRectTop` ⇒ **可自证「读到的是补偿前的值」**。
 *   ⚠️ 补偿侧的契约: 补偿模块在**写之前**把 `{ applied }` 挂在 modal 元素的
 *      `__zsTopPin` 属性上 (JS 扩展属性, **不是** DOM 属性 ⇒ 不污染 `style` / attribute)。
 *
 * ## 采样字段名固定 (brief §2.1, ⛔ 改名就等于毁掉日志可读性)
 *   t / trig / scrollY / vvTop / vvH / vvPageTop / innerH / bdTop / bdH /
 *   mmTop / mmH / mmScrollTop / mmScrollH / mmClientH / mdlTop / mcTop / mcPadB /
 *   at2 / at5 / at10 / at5chain / mdlCS / mmCS
 *   (+ v0.19.14 追加的三个**自证用**字段: mdlRectTop / pinApplied / mdlStaticTop)
 */

/* ─────────────────────────────────────────────────────────────────────────────
 * 常量
 * ───────────────────────────────────────────────────────────────────────────── */

/** URL 开关参数名 */
const PARAM = 'zskbdiag';
/** 浮层根节点的 class (样式在 less/forum.less 末尾) */
const OVERLAY_CLASS = 'ZS-kbdiag';
/** 浮层里**最多显示**的行数 (brief §3.1: 最多 11 行) */
const OVERLAY_VISIBLE = 11;
/** 每次触发后的**连续采样窗口**(ms) —— 抓「瞬间下移」的时间序列 */
const SAMPLE_MS = 1200;
/** `window.__zsKbDiag` 的条数上限 (防长会话无界增长; 超了丢最旧的) */
const MAX_ROWS = 4000;
/** 浮层的基准 `top` (px) —— 避开顶部 31~46px 的通栏露出带 */
const TOP_BASE = 52;
/** `elementFromPoint` 的取样 y (px) —— 与 at5chain 的 `(5, 5)` 同一口径 */
const PROBE_Y = 5;
/**
 * v0.19.14 补记 — 「开关复查」的节流间隔 (ms)
 *   ⚠️ **不要每帧读 `location.search`** (URL 解析不便宜) ⇒ 每 500ms 查一次就够。
 */
const URL_CHECK_MS = 500;

/* ─────────────────────────────────────────────────────────────────────────────
 * 小工具
 * ───────────────────────────────────────────────────────────────────────────── */

/** 元素 → `tag.class` (只取第一个 class ⇒ 与 brief 的 `DIV.Modal` 同款) */
function describe(el) {
  if (!el || !el.tagName) return '-';
  const tag = el.tagName;
  const raw = el.getAttribute && el.getAttribute('class') || '';
  const first = raw.trim().split(/\s+/).filter(Boolean)[0] || '';
  return first ? tag + '.' + first : tag;
}

/** 元素 → 祖先链 (最多 6 层, `>` 连接) */
function describeChain(el, max) {
  if (max === void 0) {
    max = 6;
  }
  const out = [];
  let n = el;
  while (n && out.length < max) {
    out.push(describe(n));
    n = n.parentElement;
  }
  return out.length ? out.join('>') : '-';
}

/** 取 computed style 的四个「包含块敏感」属性 */
function readCS(el) {
  if (!el || !el.getBoundingClientRect) return null;
  try {
    const s = window.getComputedStyle(el);
    return {
      transform: s.transform,
      filter: s.filter,
      backdropFilter: s.backdropFilter || s.webkitBackdropFilter,
      willChange: s.willChange,
      contain: s.contain
    };
  } catch (e) {
    return null;
  }
}

/** 数字 ⇒ 展示串 (缺值写 `-`) */
function num(v) {
  return typeof v === 'number' && isFinite(v) ? String(Math.round(v * 100) / 100) : '-';
}

/* ─────────────────────────────────────────────────────────────────────────────
 * 探针主体
 * ───────────────────────────────────────────────────────────────────────────── */

class IosKeyboardDiag {
  constructor() {
    /** @type {Array<Object>} 全部采样行 (同时挂在 window.__zsKbDiag 上) */
    this.rows = [];
    /** 第一帧的 performance.now() (t 的零点) */
    this.t0 = window.performance && window.performance.now ? window.performance.now() : Date.now();
    /** 连续采样窗口的截止时刻 */
    this.hotUntil = 0;
    /** 浮层节点 */
    this.overlay = null;
    this.rowsEl = null;
    this.copyBtn = null;
    this.toggleBtn = null;
    /** 收起状态 —— ⛔ **不写进** window.__zsKbDiag (那是诊断数据, 不是 UI 状态) */
    this.collapsed = false;
    this.rafId = 0;
    this.bound = [];
    /** v0.19.14 补记 — 「开关复查」的上次时刻 (ms) ⇒ 节流用 */
    this.lastUrlCheck = 0;
    /** v0.19.14 补记 — 是否已经 teardown 过 (幂等) */
    this.tornDown = false;
  }

  /* ── 生命周期 ───────────────────────────────────────────────────────── */

  /**
   * v0.19.14 补记 — ⭐ **开关复查 + 完整 teardown**
   *
   * ## 为什么要有 (验收方独立复核 M2)
   *   开关原本**只在页面加载时判一次** ⇒ Flarum 是 SPA ⇒ 客户端导航到别的页
   *   (URL 里的 `?zskbdiag=1` 已经掉了) 之后, **浮层仍然飘在页面上**、
   *   `window.__zsKbDiag` 仍然是数组 ⇒ 既会盖住辉哥正要截的证据,
   *   也让 brief §4 的「退出诊断开关 ⇒ 逐字节还原」在会话内**根本没有实现路径**。
   *
   * ## 做法
   *   常驻 rAF 循环里**节流**复查一次 URL (⛔ 不是每帧读 `location.search`)
   *   ⇒ 一旦 `?zskbdiag=1` 没了 ⇒ **完整拆除**:
   *     ① 摘掉全部监听 (`this.bound` 里成对摘)
   *     ② 停掉 rAF 循环 (⛔ 诊断不开了就不该常驻 60fps)
   *     ③ 移除浮层节点
   *     ④ `delete window.__zsKbDiag` / `window.__zsKbDiagHandle`
   */
  _maybeTeardown() {
    if (this.tornDown) return;
    const now = this._now();
    if (now - this.lastUrlCheck < URL_CHECK_MS) return; // ⛔ 节流: 别每帧读 URL
    this.lastUrlCheck = now;
    if (isIosKeyboardDiagEnabled()) return; // 开关还在 ⇒ 继续
    this.teardown();
  }

  /** 完整拆除探针 (幂等) */
  teardown() {
    if (this.tornDown) return;
    this.tornDown = true;

    // ① 摘监听 (成对摘 ⇒ 本仓铁律)
    for (const entry of this.bound) {
      try {
        const t = entry[0],
          type = entry[1],
          fn = entry[2],
          opts = entry[3];
        if (t && t.removeEventListener) t.removeEventListener(type, fn, opts);
      } catch (e) {/* noop */}
    }
    this.bound = [];

    // ② 停 rAF 循环 (⛔ 诊断关了就别常驻)
    if (this.rafId) {
      try {
        window.cancelAnimationFrame(this.rafId);
      } catch (e) {/* noop */}
      this.rafId = 0;
    }

    // ③ 移除浮层节点
    try {
      if (this.overlay && this.overlay.parentNode) {
        this.overlay.parentNode.removeChild(this.overlay);
      }
    } catch (e) {/* noop */}
    this.overlay = null;
    this.rowsEl = null;
    this.copyBtn = null;
    this.toggleBtn = null;

    // ④ 删全局 (⛔ 必须真的 `delete` ⇒ `typeof window.__zsKbDiag === 'undefined'`)
    try {
      delete window.__zsKbDiag;
    } catch (e) {
      window.__zsKbDiag = undefined;
    }
    try {
      delete window.__zsKbDiagHandle;
    } catch (e) {
      window.__zsKbDiagHandle = undefined;
    }
  }
  start() {
    if (this.overlay) return this;
    // ⛔ 浮层是**独立节点**, 挂在 document.body 下 —— 不动任何既有元素的样式
    this._buildOverlay();
    this._bind();
    // 开局先给一段 'raf' 采样 (浮层一打开就有东西看)
    this.hotUntil = this._now() + SAMPLE_MS;
    this._loop();
    return this;
  }

  /** 读数用的 modal —— 优先级: ① 补偿正在作用的那个 ② `.Modal.in` ③ 最后一个 `.Modal` */
  _pickModal() {
    const all = document.querySelectorAll('.Modal');
    for (let i = 0; i < all.length; i++) {
      if (all[i].__zsTopPin) return all[i];
    }
    const opened = document.querySelectorAll('.Modal.in');
    if (opened.length) return opened[opened.length - 1];
    return all.length ? all[all.length - 1] : null;
  }

  /* ── 采样 ───────────────────────────────────────────────────────────── */

  _now() {
    return window.performance && window.performance.now ? window.performance.now() : Date.now();
  }

  /**
   * 采一条快照。
   * @param {string} trig 触发来源
   */
  _snapshot(trig) {
    const now = this._now();
    const t = Math.round(now - this.t0);
    const vv = window.visualViewport || null;
    const modal = this._pickModal();
    const manager = modal && modal.closest ? modal.closest('.ModalManager') : null;
    const backdrop = document.querySelector('.Modal-backdrop');
    const content = modal && modal.querySelector ? modal.querySelector('.Modal-content') : null;

    // ⭐ 补偿写入的偏移量 (0 = 没在补偿) —— 组件② 的契约
    const pin = modal && modal.__zsTopPin ? modal.__zsTopPin : null;
    const applied = pin && typeof pin.applied === 'number' ? pin.applied : 0;
    const mdlRect = modal ? modal.getBoundingClientRect() : null;
    const mmRect = manager ? manager.getBoundingClientRect() : null;
    const bdRect = backdrop ? backdrop.getBoundingClientRect() : null;
    const mcRect = content ? content.getBoundingClientRect() : null;
    let mcPadB = '-';
    if (content) {
      try {
        mcPadB = window.getComputedStyle(content).paddingBottom;
      } catch (e) {
        mcPadB = '-';
      }
    }

    // ⭐ 露出条到底是什么 —— 在**左上角**取三个点 (2/5/10 px) + (5,5) 的祖先链
    const at2 = this._probe(2);
    const at5 = this._probe(5);
    const at10 = this._probe(10);
    let at5chain = '-';
    try {
      const el = document.elementFromPoint(5, PROBE_Y);
      at5chain = el ? describeChain(el, 6) : '-';
    } catch (e) {
      at5chain = '-';
    }
    return {
      t: t,
      trig: trig,
      scrollY: Math.round(window.scrollY || 0),
      vvTop: vv ? Math.round(vv.offsetTop || 0) : 0,
      vvH: vv ? Math.round(vv.height || 0) : 0,
      vvPageTop: vv ? Math.round(vv.pageTop || 0) : 0,
      innerH: window.innerHeight,
      bdTop: bdRect ? num(bdRect.top) : '-',
      bdH: bdRect ? num(bdRect.height) : '-',
      mmTop: mmRect ? num(mmRect.top) : '-',
      mmH: mmRect ? num(mmRect.height) : '-',
      mmScrollTop: manager ? Math.round(manager.scrollTop || 0) : '-',
      mmScrollH: manager ? Math.round(manager.scrollHeight || 0) : '-',
      mmClientH: manager ? Math.round(manager.clientHeight || 0) : '-',
      // ⭐ 核心量: **补偿前**的 `.Modal` 上边缘
      mdlTop: mdlRect ? num(mdlRect.top - applied) : '-',
      mcTop: mcRect ? num(mcRect.top) : '-',
      mcPadB: mcPadB,
      at2: at2,
      at5: at5,
      at10: at10,
      at5chain: at5chain,
      mdlCS: readCS(modal),
      mmCS: readCS(manager),
      // ── v0.19.14 追加 (自证用, 不改 brief 规定的字段名) ──
      mdlRectTop: mdlRect ? num(mdlRect.top) : '-',
      // 原始 rect.top (补偿生效后 = 已被拉回)
      pinApplied: num(applied),
      // 补偿写入的偏移量 (0 = 没在补偿)
      mdlStaticTop: mdlRect ? num(mdlRect.top - applied) : '-' // = mdlTop (显式留一份, 便于对照)
    };
  }
  _probe(x) {
    try {
      const el = document.elementFromPoint(x, PROBE_Y);
      return el ? describe(el) : '-';
    } catch (e) {
      return '-';
    }
  }

  /** 记一条 (三路输出) */
  _push(trig) {
    const row = this._snapshot(trig);
    this.rows.push(row);
    if (this.rows.length > MAX_ROWS) this.rows.splice(0, this.rows.length - MAX_ROWS);
    // ⛔ 同一个数组引用 (不换对象) ⇒ 外部拿到的 `window.__zsKbDiag` 一直是「同一个数组」
    window.__zsKbDiag = this.rows;
    try {
      console.log('[zsKbDiag]', JSON.stringify(row));
    } catch (e) {
      /* ⛔ 绝不让诊断本身把页面搞崩 */
    }
    this._renderRows();
  }

  /* ── 采样循环 ───────────────────────────────────────────────────────── */

  _loop() {
    if (this.tornDown) return;
    this.rafId = window.requestAnimationFrame(() => this._loop());
    if (this._now() < this.hotUntil) this._push('raf');
    this._syncOverlayTop();
    // ⭐ v0.19.14 补记: 开关掉了就**完整拆除** (节流复查, 不每帧读 URL)
    this._maybeTeardown();
  }

  /* ── 事件绑定 (成对摘, 本仓铁律) ─────────────────────────────────────── */

  _on(target, type, fn, opts) {
    if (!target || !target.addEventListener) return;
    target.addEventListener(type, fn, opts);
    this.bound.push([target, type, fn, opts]);
  }
  _bind() {
    const self = this;
    const trig = name => e => {
      self._trigger(name);
    };

    // ⭐ 重点: readonly input 的 focus (iOS 上 readonly **不弹键盘**, 但**可聚焦** ⇒ 仍会 scroll-into-view)
    this._on(document, 'focusin', trig('focus'), true);
    this._on(document, 'focus', trig('focus'), true);
    // 抽屉打开的那一下
    this._on(document, 'pointerdown', trig('tap'), true);
    this._on(document, 'touchstart', trig('tap'), true);
    this._on(document, 'mousedown', trig('tap'), true);
    // 视觉视口 (键盘弹起 / 收起 / 页面滚动)
    const vv = window.visualViewport;
    if (vv) {
      this._on(vv, 'resize', trig('vv.resize'));
      this._on(vv, 'scroll', trig('vv.scroll'));
    }
    // 页面滚动
    this._on(window, 'scroll', trig('win.scroll'));
    // `.Modal` 上的过渡 / 动画结束 (抽屉滑入那一瞬)
    this._on(document, 'transitionend', e => {
      if (e.target && e.target.closest && e.target.closest('.Modal')) self._trigger('transitionend');
    }, true);
    this._on(document, 'animationend', e => {
      if (e.target && e.target.closest && e.target.closest('.Modal')) self._trigger('transitionend');
    }, true);
  }

  /** 一次触发 = 立即快照 + 之后的 1200ms 每帧采样 */
  _trigger(name) {
    this._push(name);
    this.hotUntil = this._now() + SAMPLE_MS;
  }

  /* ── 浮层 ───────────────────────────────────────────────────────────── */

  _buildOverlay() {
    const overlay = document.createElement('div');
    overlay.className = OVERLAY_CLASS;
    overlay.setAttribute('data-zs-kbdiag', '1');
    const bar = document.createElement('div');
    bar.className = 'ZS-kbdiag-bar';
    const title = document.createElement('span');
    title.className = 'ZS-kbdiag-title';
    title.textContent = 'iOS 键盘诊断';
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'ZS-kbdiag-toggle';
    toggle.textContent = '≡';
    toggle.title = '收起 / 展开';
    const selfToggle = () => {
      this.collapsed = !this.collapsed;
      overlay.classList.toggle('is-collapsed', this.collapsed);
      // ⚠️ 收起状态**不写进** window.__zsKbDiag (那是诊断数据, 不是 UI 状态)
    };
    toggle.addEventListener('click', selfToggle);
    bar.appendChild(title);
    bar.appendChild(toggle);
    const rows = document.createElement('div');
    rows.className = 'ZS-kbdiag-rows';
    const foot = document.createElement('div');
    foot.className = 'ZS-kbdiag-foot';
    const copy = document.createElement('button');
    copy.type = 'button';
    copy.className = 'ZS-kbdiag-copy';
    copy.textContent = '复制';
    copy.addEventListener('click', () => {
      this._copy(copy);
    });
    foot.appendChild(copy);
    overlay.appendChild(bar);
    overlay.appendChild(rows);
    overlay.appendChild(foot);
    document.body.appendChild(overlay);
    this.overlay = overlay;
    this.rowsEl = rows;
    this.copyBtn = copy;
    this.toggleBtn = toggle;

    // 初始位置 (视觉视口跟随的第一帧)
    this._syncOverlayTop();
  }

  /** 「复制」按钮 —— ⛔ 失败只显示文案, **不许抛异常** */
  _copy(btn) {
    let text = '';
    try {
      text = JSON.stringify(window.__zsKbDiag || [], null, 2);
    } catch (e) {
      text = '';
    }
    const done = label => {
      btn.textContent = label;
    };
    try {
      if (!navigator.clipboard || !navigator.clipboard.writeText) {
        done('复制失败');
        return;
      }
      navigator.clipboard.writeText(text).then(() => done('已复制'), () => done('复制失败'));
    } catch (e) {
      done('复制失败');
    }
  }

  /** 渲染最近 N 行 (新的在上) */
  _renderRows() {
    const box = this.rowsEl;
    if (!box) return;
    const n = this.rows.length;
    const from = Math.max(0, n - OVERLAY_VISIBLE);
    const frag = document.createDocumentFragment();
    for (let i = n - 1; i >= from; i--) {
      const div = document.createElement('div');
      div.className = 'ZS-kbdiag-row';
      div.textContent = formatRow(this.rows[i]);
      frag.appendChild(div);
    }
    box.textContent = '';
    box.appendChild(frag);
  }

  /**
   * ⭐ 视觉视口跟随 —— iOS 把视觉视口往下平移 (`vv.offsetTop > 0`) 时,
   *   浮层跟着下移 ⇒ **浮层自己不会被 iOS 顶出屏幕** (否则真机最关键那一刻什么都截不到)。
   *   ⚠️ 只改**浮层自己**的 inline `top` —— ⛔ 绝不碰 `.Modal` / `.ModalManager` / `.Modal-content`。
   */
  _syncOverlayTop() {
    if (!this.overlay) return;
    const vv = window.visualViewport;
    const off = vv ? Math.round(vv.offsetTop || 0) : 0;
    const want = (off > 0 ? off + TOP_BASE : TOP_BASE) + 'px';
    if (this.overlay.style.top !== want) this.overlay.style.top = want;
  }
}

/**
 * 浮层每行的展示串 —— **字段顺序固定** (brief §3.1):
 *   `t=+123ms trig=raf scrollY=0 vvTop=0 mdlTop=24 at5=DIV.Modal`
 * 缺的字段写 `-`。
 */
function formatRow(r) {
  if (!r) return '';
  return 't=+' + r.t + 'ms' + ' trig=' + (r.trig || '-') + ' scrollY=' + (r.scrollY === undefined ? '-' : r.scrollY) + ' vvTop=' + (r.vvTop === undefined ? '-' : r.vvTop) + ' mdlTop=' + (r.mdlTop === undefined ? '-' : r.mdlTop) + ' at5=' + (r.at5 === undefined ? '-' : r.at5);
}

/* ─────────────────────────────────────────────────────────────────────────────
 * 对外接口
 * ───────────────────────────────────────────────────────────────────────────── */

/** 诊断开关是否打开 (URL 参数 `?zskbdiag=1`) */
export function isIosKeyboardDiagEnabled() {
  try {
    return new URLSearchParams(window.location.search).get(PARAM) === '1';
  } catch (e) {
    return false;
  }
}

/**
 * 装上诊断探针。
 * ⛔ **默认关** —— 没带 `?zskbdiag=1` 时: 不进 DOM、不挂监听、不写样式、
 *    `window.__zsKbDiag` 保持 `undefined` (brief G1)。
 *
 * @returns {IosKeyboardDiag|null} 打开时返回探针实例, 没打开时返回 null
 */
export function initIosKeyboardDiag() {
  if (!isIosKeyboardDiagEnabled()) return null;
  if (typeof window === 'undefined' || !document.body) return null;
  try {
    const diag = new IosKeyboardDiag();
    diag.start();
    // 挂一个句柄 (验收脚本要用它来「故意把补偿关掉再采一次」)
    window.__zsKbDiagHandle = diag;
    return diag;
  } catch (e) {
    // ⛔ 诊断本身绝不能把页面搞崩
    try {
      console.warn('[zsKbDiag] init failed:', e && e.message);
    } catch (e2) {/* noop */}
    return null;
  }
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/utils/iosKeyboardDiag', { isIosKeyboardDiagEnabled: isIosKeyboardDiagEnabled,initIosKeyboardDiag: initIosKeyboardDiag, });